// client/src/pages/admin/AdminTools.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../services/api';
import ConfirmModal from '../../components/common/ConfirmModal';

const AdminTools = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);

  // Test Notification Form
  const [notificationForm, setNotificationForm] = useState({
    userId: '',
    title: '',
    message: '',
    type: 'order_status'
  });

  // Cleanup Form
  const [cleanupDays, setCleanupDays] = useState(30);

  if (user?.role !== 'admin') {
    navigate('/admin/login');
    return null;
  }

  const handleSendTestNotification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await adminAPI.sendTestNotification(notificationForm);
      setMessage('✅ Test notification sent successfully!');
      setNotificationForm({
        userId: '',
        title: '',
        message: '',
        type: 'order_status'
      });
    } catch (err) {
      setError('Failed to send notification: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupNotifications = async () => {
    setConfirmModal({
      title: 'Delete Old Notifications',
      message: `Delete all notifications older than ${cleanupDays} days?`,
      icon: 'warning',
      confirmLabel: 'Delete',
      confirmColor: 'red',
      onConfirm: async () => {
        setConfirmModal(null);
        setLoading(true);
        setError('');
        setMessage('');
        try {
          const response = await adminAPI.cleanupNotifications(cleanupDays);
          setMessage(`${response.data.message}`);
        } catch (err) {
          setError('Failed to cleanup notifications: ' + (err.response?.data?.message || err.message));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="text-blue-600 hover:text-blue-700 mb-2 flex items-center"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Admin Tools</h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Messages */}
        {message && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {message}
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Test Notification */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              🔔 Send Test Notification
            </h2>
            <form onSubmit={handleSendTestNotification} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User ID *
                </label>
                <input
                  type="text"
                  value={notificationForm.userId}
                  onChange={(e) => setNotificationForm({ ...notificationForm, userId: e.target.value })}
                  placeholder="Enter user MongoDB ID"
                  required
                  className="w-full border border-gray-300 rounded-md p-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Get user ID from User Management page
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notification Type
                </label>
                <select
                  value={notificationForm.type}
                  onChange={(e) => setNotificationForm({ ...notificationForm, type: e.target.value })}
                  className="w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="order_status">Order Status</option>
                  <option value="order_comment">Order Comment</option>
                  <option value="order_modified">Order Modified</option>
                  <option value="invoice_uploaded">Invoice Uploaded</option>
                  <option value="new_order">New Order</option>
                  <option value="order_cancelled">Order Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={notificationForm.title}
                  onChange={(e) => setNotificationForm({ ...notificationForm, title: e.target.value })}
                  placeholder="Test Notification"
                  required
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  value={notificationForm.message}
                  onChange={(e) => setNotificationForm({ ...notificationForm, message: e.target.value })}
                  placeholder="This is a test notification from the admin panel"
                  required
                  rows="3"
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Test Notification'}
              </button>
            </form>
          </div>

          {/* Cleanup Notifications */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              🧹 Cleanup Old Notifications
            </h2>
            <p className="text-gray-600 mb-4">
              Delete notifications older than a specified number of days to keep the database clean.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delete notifications older than (days)
                </label>
                <input
                  type="number"
                  value={cleanupDays}
                  onChange={(e) => setCleanupDays(e.target.value)}
                  min="1"
                  max="365"
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>

              <button
                onClick={handleCleanupNotifications}
                disabled={loading}
                className="w-full bg-orange-600 text-white py-2 px-4 rounded-md hover:bg-orange-700 disabled:opacity-50"
              >
                {loading ? 'Cleaning...' : 'Cleanup Notifications'}
              </button>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Warning:</strong> This action cannot be undone. Make sure you want to delete old notifications before proceeding.
                </p>
              </div>
            </div>
          </div>

          {/* System Info */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              ℹ️ System Information
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Environment:</span>
                <span className="font-medium">{process.env.NODE_ENV || 'development'}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">API URL:</span>
                <span className="font-medium text-xs">{process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Current Time:</span>
                <span className="font-medium">{new Date().toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              🔗 Quick Links
            </h2>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/admin/users')}
                className="w-full text-left px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
              >
                <div className="font-medium text-blue-900">User Management</div>
                <div className="text-sm text-blue-600">View and manage all users</div>
              </button>
              <button
                onClick={() => navigate('/admin/orders')}
                className="w-full text-left px-4 py-3 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
              >
                <div className="font-medium text-green-900">Order Management</div>
                <div className="text-sm text-green-600">View and manage all orders</div>
              </button>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="w-full text-left px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors"
              >
                <div className="font-medium text-purple-900">Dashboard</div>
                <div className="text-sm text-purple-600">View system overview</div>
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminTools;